'use client'

import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocalStorage } from '../../hooks/use-local-storage'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { X, ArrowRight, ArrowLeft } from 'lucide-react'
import styles from './OnboardingTutorial.module.css'

interface TutorialStep {
  id: number
  title: string
  description: string
  target: string | null // CSS selector to highlight
  position: 'top' | 'bottom' | 'left' | 'right' | 'center' // Position of tooltip
  alternatives?: string[] // Alternative selectors if the main one fails
  fixedElement?: boolean // Is this a fixed position element (like in header)
}

// React Query keys
const TUTORIAL_KEYS = {
  completed: ['tutorial', 'completed'] as const,
  visible: ['tutorial', 'visible'] as const,
  currentStep: ['tutorial', 'currentStep'] as const,
  targetElement: ['tutorial', 'targetElement'] as const,
  tooltipPosition: ['tutorial', 'tooltipPosition'] as const,
}

export const OnboardingTutorial = () => {
  const queryClient = useQueryClient()
  
  // Define tutorial steps
  const steps: TutorialStep[] = [
    {
      id: 1,
      title: 'Welcome to NIN Terminal!',
      description: 'This quick tour will show you how to use our platform effectively.',
      target: null, // No specific target for intro
      position: 'center',
    },
    {
      id: 2,
      title: 'Connect Your Broker',
      description: 'Click on the Connectors tab to connect your broker or web3 exchange to start trading.',
      target: '[data-tutorial="connectors-tab"]', // Target using data attribute
      position: 'bottom',
      alternatives: ['.tabsContainer .tabList li:last-child', '.tabItem:last-child', '.tabList > li:nth-child(5)']
    },
    {
      id: 3,
      title: 'Adding Widgets',
      description: 'Click the Add New Widget button in the top header to add charts, order books, and more.',
      target: '[data-tutorial="new-widget-button"]', // Target the New Widget button in header
      position: 'top',
      alternatives: ['.text-gray-700.hover\\:underline', '.NewWidgetButton', '.flex'],
      fixedElement: true // This is in the fixed header
    },
    {
      id: 4,
      title: 'Customize Layout',
      description: 'Click and drag the "☰" handle to move widgets around.',
      target: '.myDragHandle',
      position: 'top',
    },
    {
      id: 5,
      title: 'Resize Widgets',
      description: 'Drag the corners of any widget to resize it to your preference.',
      target: '.react-resizable-handle',
      position: 'bottom',
    },
    {
      id: 6,
      title: 'Plugin Tabs',
      description: 'Access advanced features like Backtester, Options Builder, and more.',
      target: '[data-tutorial="plugins-tab"]', // Target using data attribute
      position: 'bottom',
      alternatives: ['.tabsContainer .tabList li:first-child', '.tabItem:first-child', '.tabList > li:first-of-type']
    },
    {
      id: 7,
      title: 'AI Terminal',
      description: 'Click the AI Terminal button in the header to access Agentic trading tools. Note: AI Terminal only works with NINX connector.',
      target: '[data-tutorial="ai-terminal-button"]', // Target the AI Terminal button in header
      position: 'right',
      alternatives: ['.ai-terminal-button', 'button[data-tutorial="ai-terminal-button"]'],
      fixedElement: true // This is in the fixed header
    },
    {
      id: 8,
      title: 'AI Stockbot Chat',
      description: 'Click the floating chat button to get AI-powered trading insights and analysis.',
      target: '[data-tutorial="stockbot-chat-button"]', // Target the floating chat button
      position: 'right',
      alternatives: ['.chatButton', 'button[data-tutorial="stockbot-chat-button"]']
    },
    {
      id: 9,
      title: "You're all set!",
      description: 'Explore the platform and customize it to your trading needs.',
      target: null,
      position: 'center',
    },
  ]

  // React Query for tutorial completion status
  const { data: hasCompletedTutorial = false } = useQuery({
    queryKey: TUTORIAL_KEYS.completed,
    queryFn: () => {
      if (typeof window === 'undefined') return false
      return localStorage.getItem('nin-tutorial-completed') === 'true'
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })

  // React Query for tutorial visibility
  const { data: isTutorialVisible = false } = useQuery({
    queryKey: TUTORIAL_KEYS.visible,
    queryFn: () => !hasCompletedTutorial,
    enabled: typeof window !== 'undefined',
    staleTime: 1000, // Check every second
  })

  // React Query for current step
  const { data: currentStep = 0 } = useQuery({
    queryKey: TUTORIAL_KEYS.currentStep,
    queryFn: () => 0,
    staleTime: Infinity,
  })

  // React Query for target element (with proper invalidation for position updates)
  const { data: targetElement = null } = useQuery({
    queryKey: [...TUTORIAL_KEYS.targetElement, currentStep],
    queryFn: async (): Promise<HTMLElement | null> => {
      const step = steps[currentStep]
      if (!step?.target) return null

      const element = await findElementWithRetry(step)
      
      // For non-fixed elements, handle scrolling here and wait for completion
      if (element && !step.fixedElement) {
        // Scroll into view and wait for animation to complete
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        
        // Wait for scroll animation to settle with scroll detection
        await new Promise<void>(resolve => {
          let scrollTimeout: NodeJS.Timeout
          let lastScrollY = window.scrollY
          
          const checkScrollComplete = () => {
            const currentScrollY = window.scrollY
            
            if (Math.abs(currentScrollY - lastScrollY) < 1) {
              // Scroll has stopped, wait a bit more for any final adjustments
              clearTimeout(scrollTimeout)
              setTimeout(resolve, 200)
            } else {
              // Still scrolling, check again
              lastScrollY = currentScrollY
              scrollTimeout = setTimeout(checkScrollComplete, 50)
            }
          }
          
          // Start checking after initial delay
          setTimeout(checkScrollComplete, 100)
          
          // Fallback timeout to prevent infinite waiting
          setTimeout(resolve, 2000)
        })
        
        // Verify element is still in DOM and positioned correctly
        if (document.contains(element)) {
          return element
        }
      }
      
      return element
    },
    enabled: isTutorialVisible && !!steps[currentStep]?.target,
    staleTime: 500, // Shorter cache to allow more frequent updates
    gcTime: 2000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })

  // React Query for stable tooltip position (with better invalidation)
  const { data: stableTooltipPosition } = useQuery({
    queryKey: [...TUTORIAL_KEYS.tooltipPosition, currentStep, !!targetElement],
    queryFn: () => {
      // Force a small delay to ensure DOM is settled
      return new Promise<any>(resolve => {
        setTimeout(() => {
          resolve(calculateTooltipPosition())
        }, 100)
      })
    },
    enabled: isTutorialVisible,
    staleTime: 800, // Keep position stable for a shorter time
    gcTime: 1500,
    refetchOnWindowFocus: false,
  })

  // React Query for highlight style (separate from tooltip position)
  const { data: highlightStyle } = useQuery({
    queryKey: ['tutorial', 'highlightStyle', currentStep, !!targetElement],
    queryFn: () => {
      return new Promise<any>(resolve => {
        setTimeout(() => {
          resolve(getHighlightStyle())
        }, 150) // Slightly longer delay for highlight to ensure position is final
      })
    },
    enabled: isTutorialVisible && !!targetElement,
    staleTime: 800,
    gcTime: 1500,
    refetchOnWindowFocus: false,
  })

  // Mutations for state updates with proper invalidation
  const setTutorialVisibleMutation = useMutation({
    mutationFn: (visible: boolean) => {
      queryClient.setQueryData(TUTORIAL_KEYS.visible, visible)
      return Promise.resolve(visible)
    }
  })

  const setCurrentStepMutation = useMutation({
    mutationFn: (step: number) => {
      queryClient.setQueryData(TUTORIAL_KEYS.currentStep, step)
      return Promise.resolve(step)
    },
    onSuccess: () => {
      // Invalidate all position-related queries when step changes
      queryClient.invalidateQueries({ queryKey: TUTORIAL_KEYS.targetElement })
      queryClient.invalidateQueries({ queryKey: TUTORIAL_KEYS.tooltipPosition })
      queryClient.invalidateQueries({ queryKey: ['tutorial', 'highlightStyle'] })
    }
  })

  const completeTutorialMutation = useMutation({
    mutationFn: () => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('nin-tutorial-completed', 'true')
      }
      queryClient.setQueryData(TUTORIAL_KEYS.completed, true)
      queryClient.setQueryData(TUTORIAL_KEYS.visible, false)
      return Promise.resolve(true)
    }
  })

  // Get the highlight style, properly handling fixed elements
  const getHighlightStyle = () => {
    if (!targetElement) return { display: 'none' };
    
    try {
      // Force a fresh getBoundingClientRect call to get the latest position
      const rect = targetElement.getBoundingClientRect();
      const step = steps[currentStep];
      
      // For fixed elements like in the header, use viewport coordinates directly
      if (step?.fixedElement) {
        return {
          position: 'fixed' as const,
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          zIndex: 9999,
        };
      }
      
      // For non-fixed elements, use absolute positioning
      // Add current scroll position to convert viewport coordinates to absolute
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      
      return {
        position: 'absolute' as const,
        top: rect.top + scrollY - 4,
        left: rect.left + scrollX - 4,
        width: rect.width + 8,
        height: rect.height + 8,
        zIndex: 9999,
      };
    } catch (error) {
      console.warn('Error calculating highlight style:', error);
      return { display: 'none' };
    }
  };

  // Enhanced element finding function
  const findElementWithRetry = async (step: TutorialStep, maxAttempts = 15, interval = 200): Promise<HTMLElement | null> => {
    return new Promise((resolve) => {
      let attempts = 0
      let lastPosition: { top: number; left: number } | null = null
      let stableCount = 0
      const requiredStableChecks = 3

      const tryFind = () => {
        attempts++

        let element: HTMLElement | null = null
        if (step.target) {
          element = document.querySelector(step.target) as HTMLElement
        }

        if (!element && step.alternatives) {
          for (const altSelector of step.alternatives) {
            element = document.querySelector(altSelector) as HTMLElement
            if (element) break
          }
        }

        if (element) {
          const rect = element.getBoundingClientRect()
          const currentPosition = { top: rect.top, left: rect.left }

          if (step.fixedElement || step.position === 'center') {
            resolve(element)
            return
          }

          if (lastPosition &&
              Math.abs(currentPosition.top - lastPosition.top) < 2 &&
              Math.abs(currentPosition.left - lastPosition.left) < 2) {
            stableCount++
            if (stableCount >= requiredStableChecks) {
              resolve(element)
              return
            }
          } else {
            stableCount = 0
          }

          lastPosition = currentPosition
        }

        if (attempts >= maxAttempts) {
          resolve(element)
          return
        }

        setTimeout(tryFind, interval)
      }

      setTimeout(tryFind, step.fixedElement ? 100 : 300) // Shorter delay since scroll is handled elsewhere
    })
  }

  // Calculate stable tooltip position
  const calculateTooltipPosition = () => {
    const step = steps[currentStep]
    if (!step) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }

    // Center position for intro steps with no target
    if (!targetElement || step.position === 'center') {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }
    }

    const rect = targetElement.getBoundingClientRect()
    const scrollY = step.fixedElement ? 0 : window.scrollY

    switch (step.position) {
      case 'top':
        return {
          top: `${rect.top + scrollY - 12 + 128}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translateX(-50%)'
        }
      case 'bottom':
        return {
          top: `${rect.bottom + scrollY - 200}px`,
          left: `${rect.left + rect.width / 2 + 200}px`,
          transform: 'translateX(-50%)'
        }
      case 'left':
        return {
          top: `${rect.top + scrollY + rect.height / 2}px`,
          left: `${rect.left - 12 - 320}px`,
          transform: 'translateY(-50%)'
        }
      case 'right':
        // Special handling for chat button to prevent bottom overflow
        const isChatButton = step.target?.includes('stockbot-chat-button')
        const topOffset = isChatButton ? rect.top + scrollY - 180 : rect.top + scrollY + rect.height / 2
        const transform = isChatButton ? 'translateY(0)' : 'translateY(-50%)'
        
        return {
          top: `${topOffset}px`,
          left: `${rect.right - 270}px`,
          transform: 'translateX(-50%)'
        }
      default:
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }
    }
  }

  // Auto-show tutorial for first-time users
  useEffect(() => {
    if (!hasCompletedTutorial) {
      const timer = setTimeout(() => {
        setTutorialVisibleMutation.mutate(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [hasCompletedTutorial])

  // Handle next step
  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      completeTutorialMutation.mutate()
    } else {
      setCurrentStepMutation.mutate(currentStep + 1)
    }
  }
  
  // Handle previous step
  const handlePrev = () => {
    setCurrentStepMutation.mutate(Math.max(0, currentStep - 1))
  }
  
  // Handle temporary hiding (X button) - only hides without marking as completed
  const hideTemporarily = () => {
    setTutorialVisibleMutation.mutate(false)
    // Does NOT update localStorage, so tutorial will show again on refresh
  }
  
  // Handle skip and completion - marks as completed
  const completeTutorial = () => {
    completeTutorialMutation.mutate()
  }

  if (!isTutorialVisible) return null

  const currentStepData = steps[currentStep]
  
  return (
    <>
      {/* Overlay/backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998]"
      />
      
      {/* Target element highlight */}
      {targetElement && (
        <div
          key={currentStep}
          className={styles.tutorialHighlight}
          style={highlightStyle || {}}
        />
      )}
      
      {/* Tutorial tooltip */}
      <div
        className={cn("fixed z-[10000] bg-background rounded-lg shadow-lg p-6 w-80 transition-all duration-300", styles.tooltipContainer)}
        style={stableTooltipPosition || { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        {/* Close button - now uses hideTemporarily instead of completeTutorial */}
        <button
          onClick={hideTemporarily}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label="Close tutorial"
        >
          <X size={18} />
        </button>
        
        {/* Content */}
        <h3 className="font-bold text-lg mb-2">{currentStepData.title}</h3>
        <p className="text-sm text-muted-foreground mb-6">{currentStepData.description}</p>
        
        {/* Step indicators */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={cn(
                "w-2 h-2 rounded-full",
                styles.stepIndicator,
                idx === currentStep ? styles.active : "bg-muted"
              )}
            />
          ))}
        </div>
        
        {/* Buttons */}
        <div className="flex justify-between">
          <div>
            {currentStep > 0 && (
              <Button variant="outline" size="sm" onClick={handlePrev}>
                <ArrowLeft size={16} className="mr-2" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex gap-2">
            {currentStep < steps.length - 1 && (
              <Button variant="outline" size="sm" onClick={completeTutorial}>
                Skip
              </Button>
            )}
            <Button size="sm" onClick={handleNext}>
              {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
              {currentStep !== steps.length - 1 && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default OnboardingTutorial 